import { useMemo } from 'react'

import { DateTime } from 'luxon'

import {
  matchFilterArrayIncludes, matchFilterNumber, matchFilterString, sortArrayBy,
} from '@domain-group/javascript-utils'

import { useLoadMore, useWatchEntityUpdates } from '@domain-group/react-hooks'

import useReduxAction from '@hooks/useReduxAction'
import useSelector from '@hooks/useSelector'

import type { CampaignQuoteTemplateModel, CampaignQuoteTemplateRequestOptions } from '@models/types'
import type { ModuleState } from '@redux/modules/types'

const watchEntityKeys = ['campaignQuoteTemplates']

type CampaignQuoteTemplateFilters = {
  agency_id?: number,
  builder_version?: 1 | 2,
  created_after?: string,
  created_before?: string,
  hidden?: 'true' | 'false',
  string?: string,
  user_ids?: number[],
  assigned_user_id?: number,
}

type UseCampaignQuoteTemplatesOptions = {
  filters?: CampaignQuoteTemplateFilters,
  performHttpRequests?: boolean,
  requestOptions?: CampaignQuoteTemplateRequestOptions,
}

function useCampaignQuoteTemplates(options: UseCampaignQuoteTemplatesOptions) {
  const { filters = {}, requestOptions } = options
  const {
    agency_id: filterAgencyId,
    assigned_user_id: filterAssignedUserId,
    builder_version: filterBuilderVersion = 2,
    created_after: filterCreatedAfter,
    created_before: filterCreatedBefore,
    hidden: filterHidden = 'false',
    string: filterString,
  } = filters

  const {
    updatedEntities: { campaignQuoteTemplates: campaignQuoteTemplatesUpdatedAt },
  } = useWatchEntityUpdates(watchEntityKeys)

  const { campaignQuoteTemplates } = useSelector(reduxState => reduxState.entities)

  const filteredCampaignQuoteTemplates = useMemo(() => {
    const filtered = Object.values(campaignQuoteTemplates).filter((
      campaignQuoteTemplate: CampaignQuoteTemplateModel,
    ) => {
      const {
        agency_id, builder_version, created_at, hidden, id, title, user_ids = [],
      } = campaignQuoteTemplate

      const matchId = id.toString() === filterString
      const matchTitle = matchFilterString(title, filterString)
      const matchString = matchId || matchTitle

      const matchAgency = matchFilterNumber(agency_id, filterAgencyId)

      const matchBuilderVersion = matchFilterNumber(builder_version, filterBuilderVersion)
      const matchHidden = filterHidden ? filterHidden === String(hidden) : true

      const matchUser = filterAssignedUserId ? matchFilterArrayIncludes(user_ids, filterAssignedUserId) : true

      const createdAtDT = created_at ? DateTime.fromISO(created_at, { zone: 'utc' }) : undefined
      const matchIsCreatedAfter = filterCreatedAfter && createdAtDT
        // @ts-expect-error TS2365
        ? createdAtDT >= filterCreatedAfter
        : true

      const matchIsCreatedBefore = filterCreatedBefore && createdAtDT
        // @ts-expect-error TS2365
        ? createdAtDT <= filterCreatedBefore
        : true

      return matchAgency
      && matchBuilderVersion
      && matchHidden
      && matchString
      && matchUser
      && matchIsCreatedAfter
      && matchIsCreatedBefore
    })

    return sortArrayBy(filtered, 'asc', 'title')
  }, [campaignQuoteTemplatesUpdatedAt, JSON.stringify(filters)])

  const filteredCampaignQuoteTemplatesCount = filteredCampaignQuoteTemplates.length
  const hasFilteredCampaignQuoteTemplates = !!filteredCampaignQuoteTemplatesCount

  const loadMorePayload = useLoadMore({
    filters,
    loadedCount: filteredCampaignQuoteTemplatesCount,
    performHttpRequests: options.performHttpRequests,
  })

  const {
    callbacks: { loadMore },
    canLoadMore,
    filtersWithOffset,
    limit,
    performHttpRequests,
  } = loadMorePayload

  const { loading: loadingCampaignQuoteTemplates } = useReduxAction(
    'campaignQuoteTemplates',
    'loadCampaignQuoteTemplates',
    {
      ...requestOptions,
      ...filtersWithOffset,
      limit,
    },
    [filtersWithOffset, performHttpRequests],
    {
      shouldPerformFn: ({ loading }: ModuleState) => performHttpRequests && !loading,
    },
  )

  return {
    callbacks: {
      loadMore,
    },
    canLoadMore,
    filteredCampaignQuoteTemplates,
    filteredCampaignQuoteTemplatesCount,
    hasFilteredCampaignQuoteTemplates,
    loading: loadingCampaignQuoteTemplates,
  }
}

export default useCampaignQuoteTemplates
